import { $, icons } from './helpers';
import { syncAppBar } from './shell';
import { setBigTitle } from './helpers';

// ─── Toast ──────────────────────────────────────────────────────────────────
export function toast(message: string, type?: string, opts?: { duration?: number; action?: { label: string; onClick: () => void } }) {
  const container = $('#toast-container')!;
  const el = document.createElement('div');
  const dur = opts?.duration || 4000;
  el.className = 'toast toast-' + (type || 'info');

  const span = document.createElement('span');
  span.textContent = message;
  el.appendChild(span);

  if (opts?.action) {
    const btn = document.createElement('button');
    btn.className = 'btn btn-ghost btn-sm toast-action';
    btn.textContent = opts.action.label;
    btn.addEventListener('click', () => { opts.action!.onClick(); close(); });
    el.appendChild(btn);
  }

  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn-icon toast-close';
  closeBtn.style.cssText = 'margin-left:auto;width:24px;height:24px';
  closeBtn.innerHTML = '<i data-lucide="x"></i>';  // SAFE: static content only
  el.appendChild(closeBtn);

  container.appendChild(el);
  icons();

  const close = () => { el.remove(); };
  closeBtn.addEventListener('click', close);
  setTimeout(close, dur);
}

// ─── Modal ──────────────────────────────────────────────────────────────────
// SAFE: all callers must pre-escape dynamic content via esc() before passing html
export function openModal(title: string, html: string, size?: string) {
  $('#modal-title')!.textContent = title;
  const body = $('#modal-body')!;
  body.innerHTML = html;  // SAFE: callers pre-escape all dynamic values via esc()
  const modal = $('#modal')!;
  modal.className = 'modal' + (size ? ' modal-' + size : '');
  ($('#modal-overlay') as HTMLElement).style.display = 'flex';
  icons();
}

export function closeModal() {
  const overlay = $('#modal-overlay') as HTMLElement;
  const modal = $('#modal')!;
  overlay.style.display = 'none';
}

(window as any).closeModal = closeModal;
$('#modal-close')?.addEventListener('click', closeModal);
// Intentionally no backdrop-click-to-close: clicking outside a form modal must not
// discard in-progress input. Modals close only via the X button or Cancel.

// ─── Confirm Dialog ─────────────────────────────────────────────────────────
export function confirmDialog(title: string, message: string, confirmLabel?: string, type?: string): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = $('#confirm-overlay') as HTMLElement;
    $('#confirm-title')!.textContent = title;
    $('#confirm-message')!.textContent = message;
    const okBtn = $('#confirm-ok') as HTMLButtonElement;
    okBtn.textContent = confirmLabel || 'Onayla';
    okBtn.className = 'btn ' + (type === 'danger' ? 'btn-danger' : 'btn-primary');
    const iconEl = $('#confirm-icon')!;
    const iconName = type === 'danger' ? 'alert-triangle' : 'help-circle';
    const iconColor = type === 'danger' ? 'var(--danger)' : 'var(--accent)';
    iconEl.innerHTML = `<i data-lucide="${iconName}" style="color:${iconColor}"></i>`;  // SAFE: static values only
    overlay.style.display = 'flex';
    icons();
    const cleanup = (val: boolean) => { overlay.style.display = 'none'; resolve(val); };
    okBtn.onclick = () => cleanup(true);
    ($('#confirm-cancel') as HTMLElement).onclick = () => cleanup(false);
    overlay.onclick = (e) => { if (e.target === overlay) cleanup(false); };
  });
}

// ─── Skeleton Loaders ───────────────────────────────────────────────────────
// SAFE: no dynamic content, purely static structure
export function skeletonStats(count: number): string {
  let h = '<div class="stats-grid">';
  for (let i = 0; i < count; i++) {
    h += '<div class="stat-card"><div class="skeleton skeleton-text" style="width:60%;height:14px;margin-bottom:12px"></div><div class="skeleton skeleton-text" style="width:40%;height:28px"></div></div>';
  }
  return h + '</div>';
}

export function skeletonTable(cols: number, rows: number): string {
  let h = '<div class="card"><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr>';
  for (let c = 0; c < cols; c++) h += '<th><div class="skeleton skeleton-text" style="width:80px;height:12px"></div></th>';
  h += '</tr></thead><tbody>';
  for (let r = 0; r < rows; r++) {
    h += '<tr>';
    for (let c = 0; c < cols; c++) {
      const w = 60 + Math.floor(Math.random() * 40);
      h += `<td><div class="skeleton skeleton-text" style="width:${w}%;height:14px"></div></td>`;
    }
    h += '</tr>';
  }
  return h + '</tbody></table></div></div></div>';
}

export function skeletonCards(count: number): string {
  let h = '';
  for (let i = 0; i < count; i++) {
    h += '<div class="card" style="margin-bottom:0.8rem"><div class="card-body"><div class="skeleton skeleton-text" style="width:60%;height:18px;margin-bottom:12px"></div><div class="skeleton skeleton-text" style="width:90%;height:14px;margin-bottom:8px"></div><div class="skeleton skeleton-text" style="width:40%;height:14px"></div></div></div>';
  }
  return h;
}

// ─── Breadcrumb ─────────────────────────────────────────────────────────────
// SAFE: labels come from our own code, not user input
export function setBreadcrumb(items: Array<{ label: string; href?: string }>) {
  // Aynı veri masaüstünde breadcrumb, telefonda üst çubuk başlığı + geri oku
  // ve büyük başlık olarak kullanılıyor.
  syncAppBar(items);
  setBigTitle(items[items.length - 1]?.label || '');
  const el = $('#breadcrumb');
  if (!el) return;
  el.textContent = '';
  items.forEach((item, i) => {
    if (i > 0) {
      const sep = document.createElement('span');
      sep.className = 'breadcrumb-separator';
      sep.textContent = '/';
      el.appendChild(sep);
    }
    if (item.href && i < items.length - 1) {
      const a = document.createElement('a');
      a.href = item.href;
      a.textContent = item.label;
      el.appendChild(a);
    } else {
      const span = document.createElement('span');
      span.className = 'breadcrumb-current';
      span.textContent = item.label;
      el.appendChild(span);
    }
  });
}

// ─── Table Sorting ──────────────────────────────────────────────────────────
export function makeSortable(tableEl: Element) {
  const headers = tableEl.querySelectorAll('th[data-sort]');
  headers.forEach(th => {
    th.addEventListener('click', () => {
      const idx = Array.from(th.parentElement!.children).indexOf(th);
      const tbody = tableEl.querySelector('tbody')!;
      const rows = Array.from(tbody.querySelectorAll('tr'));
      const isAsc = th.classList.contains('asc');
      headers.forEach(h => { h.classList.remove('asc', 'desc'); });
      const dir = isAsc ? 'desc' : 'asc';
      th.classList.add(dir);
      rows.sort((a, b) => {
        const aVal = (a.children[idx]?.textContent || '').trim();
        const bVal = (b.children[idx]?.textContent || '').trim();
        const aNum = parseFloat(aVal.replace(/[^0-9.-]/g, ''));
        const bNum = parseFloat(bVal.replace(/[^0-9.-]/g, ''));
        if (!isNaN(aNum) && !isNaN(bNum)) return dir === 'asc' ? aNum - bNum : bNum - aNum;
        return dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
      rows.forEach(r => tbody.appendChild(r));
    });
  });
}

// ─── Pagination ─────────────────────────────────────────────────────────────
export function renderPagination(containerId: string, currentPage: number, totalPages: number, callback: (p: number) => void): string {
  if (totalPages <= 1) return '';
  let html = '<div class="pagination">';
  html += `<button class="pagination-btn" ${currentPage <= 1 ? 'disabled' : ''} data-page="${currentPage - 1}">&laquo;</button>`;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
      html += `<button class="pagination-btn${i === currentPage ? ' active' : ''}" data-page="${i}">${i}</button>`;
    } else if (i === currentPage - 2 || i === currentPage + 2) {
      html += '<span class="pagination-info">...</span>';
    }
  }
  html += `<button class="pagination-btn" ${currentPage >= totalPages ? 'disabled' : ''} data-page="${currentPage + 1}">&raquo;</button></div>`;
  setTimeout(() => {
    document.getElementById(containerId)?.querySelectorAll('.pagination-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const p = parseInt(btn.getAttribute('data-page') || '1');
        if (p >= 1 && p <= totalPages) callback(p);
      });
    });
  }, 0);
  return html;
}

// ─── Search Filter ──────────────────────────────────────────────────────────
export function bindSearch(inputId: string, itemSelector: string, containerSelector: string) {
  const input = document.getElementById(inputId) as HTMLInputElement;
  if (!input) return;
  let timer: any;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const q = input.value.toLowerCase().trim();
      const container = document.querySelector(containerSelector);
      if (!container) return;
      container.querySelectorAll(itemSelector).forEach((el: any) => {
        const text = el.textContent?.toLowerCase() || '';
        el.style.display = text.includes(q) ? '' : 'none';
      });
    }, 200);
  });
}
